import { FC, KeyboardEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useInfiniteAiAgentMessages } from '@/entities/ai-agent-chat';
import { useAiAgentStreaming } from '@/features/ai-agent-chat';
import { useCopilot } from '@/features/copilot';
import { useGuardedInfiniteScroll } from '@/shared/hooks';
import { CopilotChatPresenter } from './CopilotChatPresenter';

/** Упреждение подгрузки более ранней истории, px */
const HISTORY_LOAD_THRESHOLD = 300;

type Props = {
  className?: string;
};

export const CopilotChat: FC<Props> = ({ className }) => {
  const { activeChatId, isCreatingChat, showChatList, createNewChat } = useCopilot();

  const [inputValue, setInputValue] = useState('');

  const viewportRef = useRef<HTMLDivElement>(null);

  const {
    messages: history,
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteAiAgentMessages(activeChatId ?? 0);

  const { isGenerating, streamingTokens, pendingUserMessages, hasReplyFailed, sendChatMessage } = useAiAgentStreaming(
    activeChatId ?? 0,
  );

  const isInputDisabled = isGenerating || isCreatingChat;

  const messages = useMemo(() => [...history, ...pendingUserMessages], [history, pendingUserMessages]);

  // Обратное направление: маркер подгрузки в начале ленты, место просмотра удерживает сам хук
  const { sentinelRef, isAutoLoadStopped, resumeAutoLoad } = useGuardedInfiniteScroll({
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    pagesCount: data?.pages.length ?? 0,
    resetKey: activeChatId,
    threshold: HISTORY_LOAD_THRESHOLD,
    direction: 'reverse',
    root: viewportRef,
  });

  /**
   * Вниз лента едет только на своё: открытие чата, отправленное сообщение и ответ агента.
   * Догруженная более ранняя история сюда не входит — прочитанное место остаётся перед глазами.
   */
  const autoDownScrollKey = `${activeChatId}|${history.at(-1)?.id ?? 0}|${pendingUserMessages.length}|${streamingTokens.length}|${hasReplyFailed}`;

  const pendingMessageRef = useRef<string | null>(null);

  const handleSend = useCallback(async () => {
    const trimmed = inputValue.trim();
    if (!trimmed || isInputDisabled) return;

    setInputValue('');

    if (!activeChatId) {
      pendingMessageRef.current = trimmed;
      await createNewChat();
    } else {
      sendChatMessage(trimmed);
    }
  }, [inputValue, isInputDisabled, activeChatId, createNewChat, sendChatMessage]);

  useEffect(() => {
    if (activeChatId && pendingMessageRef.current) {
      const msg = pendingMessageRef.current;
      pendingMessageRef.current = null;
      sendChatMessage(msg);
    }
  }, [activeChatId, sendChatMessage]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend],
  );

  return (
    <CopilotChatPresenter
      className={className}
      messages={messages}
      isGenerating={isGenerating}
      hasReplyFailed={hasReplyFailed}
      isCreating={isCreatingChat}
      isInputDisabled={isInputDisabled}
      streamingTokens={streamingTokens}
      inputValue={inputValue}
      viewportRef={viewportRef}
      sentinelRef={sentinelRef}
      isLoadingHistory={isFetchingNextPage}
      isHistoryLoadStopped={isAutoLoadStopped}
      autoDownScrollKey={autoDownScrollKey}
      onRetryHistoryLoad={resumeAutoLoad}
      onInputChange={setInputValue}
      onKeyDown={handleKeyDown}
      onSend={handleSend}
      onBackToList={showChatList}
    />
  );
};
